import { api, requestBlob } from "./client";

/**
 * The WhatsApp inbox — bella-admin-api `/whatsapp/*` (NestJS), all behind
 * `whatsapp.view`. Every thread is one of OUR business numbers and one
 * contact; messages from every provider (WATI, Chatzy, …) arrive in one shape.
 *
 * Phone numbers never arrive unmasked: a contact is its WhatsApp profile name
 * and "******3210". Sending (replies, templates, a ticket from a message) is
 * behind `whatsapp.send`; the server checks every rule again.
 */

export type WhatsAppHandler = "ai" | "human" | "none";

export interface WhatsAppNumber {
  id: number;
  /** "WATI Customer", "Chatzy Customer WA1". */
  name: string;
  /** "wati", "chatzy", "heltar", or "". */
  provider: string;
  maskedNumber: string;
  isActive: boolean;
}

export interface ThreadNumber {
  id: number;
  name: string;
  provider: string;
}

export interface ThreadPerson {
  type: "customer" | "champ";
  id: number;
  name: string;
}

export interface ThreadSummary {
  id: number;
  number: ThreadNumber;
  /** WhatsApp profile name ("" when unknown), and "******3210". */
  contact: { name: string; maskedPhone: string };
  /** The customer and/or champ with this phone — 0 to 2 of them. */
  people: ThreadPerson[];
  lastMessage: { preview: string; direction: "in" | "out" | null; at: number | null };
  unreadCount: number;
  handledBy: WhatsAppHandler;
  /** The bot's name for `ai`; "" when the provider does not say. */
  handlerName: string;
  /** Epoch ms the 24-hour reply window closes; null — they never wrote to us. */
  replyWindowEndsAt: number | null;
  updatedAt: number;
  /** In a template or tool filtered list: the message that matched. */
  match?: ThreadMatch;
}

export interface ThreadMatch {
  kind: "template" | "tool";
  name: string;
  messageId: number;
  at: number;
  status: MessageStatus | null;
  /** A template answered within 24 hours; null for a tool. */
  replied: boolean | null;
}

export interface RelatedThread {
  id: number;
  number: ThreadNumber;
  lastMessageAt: number | null;
  unreadCount: number;
}

/**
 * The bucket for older Chatzy chats whose business number Chatzy does not
 * report: `number.id === 0`, named "Chatzy · number not known". A thread moves
 * to its real number once that becomes known.
 */
export const NUMBER_NOT_KNOWN_ID = 0;

/** A ticket a message was filed under (legacy's ticket, `ticketno`). */
export interface MessageTicket {
  id: number;
  /** "HELP1208212", "PT…", "WA…". */
  number: string;
}

export interface HistorySyncResult {
  phones: number;
  chatzy: {
    contacts: number;
    conversations: number;
    placed: number;
    /** Conversations kept in the "number not known" bucket. */
    numberNotKnown: number;
    messagesAdded: number;
  };
  wati: {
    fromTicketReplies: number;
    fromApi: number;
    pagesRead: number;
    /** More history than was read; only the newest came in. */
    truncated: boolean;
  };
  ticketLinks: number;
  /** Per-provider problems; the rest of the sync still ran. */
  errors: string[];
}

/** One "Sync history" run for a person (or, with nobody linked, a number). */
export interface HistorySync {
  id: number;
  status: "queued" | "running" | "done" | "failed";
  subject: { type: "customer" | "champ" | "contact"; id: number | null };
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  result: HistorySyncResult | null;
  /** Why it failed; "" otherwise. */
  error: string;
}

/** One thing an admin may or may not send in a thread right now. */
export interface ComposerAction {
  allowed: boolean;
  /** Why not, in words for the admin; "" when allowed. */
  reason: string;
}

/**
 * What may be sent in a thread now — the same rules the send routes check.
 * Stale once the thread's handler or reply window changes; re-read the thread.
 */
export interface ThreadComposer {
  text: ComposerAction;
  template: ComposerAction;
  /** "wati", "chatzy", or "" when nothing can be sent. */
  provider: string;
}

export interface ThreadDetail extends ThreadSummary {
  /** The same person on our other business numbers. */
  related: RelatedThread[];
  /** Tickets this thread's messages were filed under, newest first, ≤ 20. */
  tickets: MessageTicket[];
  /** The newest sync of the thread's person (or its number when nobody is linked). */
  lastSync: HistorySync | null;
  composer: ThreadComposer;
}

export const MESSAGE_KINDS = [
  "text",
  "image",
  "video",
  "audio",
  "document",
  "sticker",
  "location",
  "contacts",
  "template",
  "interactive",
  "button_reply",
  "list_reply",
  "reaction",
  "tool",
  "event",
  "unknown",
] as const;
export type MessageKind = (typeof MESSAGE_KINDS)[number];

export type MessageAuthor =
  "contact" | "ai" | "staff" | "panel" | "automation" | "system";
export type MessageStatus = "sent" | "delivered" | "read" | "failed";

export interface MessageTemplate {
  name: string;
  language?: string;
  params?: string[];
  header?: { type: string; text?: string; url?: string };
}

export interface WhatsAppMessage {
  id: number;
  sessionId: number | null;
  /** `none`: a tool result or an event — on the timeline, never sent. */
  direction: "in" | "out" | "none";
  author: MessageAuthor;
  authorName: string;
  kind: MessageKind;
  body: string | null;
  /** `proxied`: fetch through `/whatsapp/messages/:id/media` (needs the token). */
  media: {
    url: string | null;
    proxied: boolean;
    fileName: string | null;
    mimeType: string | null;
  } | null;
  template: MessageTemplate | null;
  /** Buttons or list rows an outgoing message offered. */
  buttons: string[] | null;
  /** What the contact tapped. */
  reply: { title: string; id?: string } | null;
  location: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  } | null;
  /** Name and input only when bella served the tool and logged the call. */
  tool: { name: string | null; input: unknown; output: unknown } | null;
  /** Outgoing only. */
  status: MessageStatus | null;
  statusDetail: string;
  sentAt: number;
  /** Tickets the message was filed under; usually none. */
  tickets: MessageTicket[];
  /** The UUID a panel message was sent with; null for every other message. */
  clientMessageId: string | null;
}

export interface WhatsAppSession {
  id: number;
  /** "whatsapp", "whatsapp-template". */
  kind: string;
  /** "live", "closed", or "". */
  status: string;
  handledBy: WhatsAppHandler;
  botName: string;
  startedAt: number;
}

export const THREAD_FILTERS = ["all", "unread", "needs_reply", "ai", "human"] as const;
export type ThreadFilter = (typeof THREAD_FILTERS)[number];

/** What became of a template: delivered (or read), read, failed, or answered within 24 h. */
export const TEMPLATE_OUTCOMES = ["delivered", "read", "failed", "replied"] as const;
export type TemplateOutcome = (typeof TEMPLATE_OUTCOMES)[number];

/** IST days ("YYYY-MM-DD"), with optional "HH:MM" times — the call logs' form. */
export interface InboxWindow {
  from: string;
  to: string;
  fromTime?: string;
  toTime?: string;
}

export interface ThreadListParams {
  numberId: number | null;
  filter: ThreadFilter;
  search: string;
  /** Chats sent this template (in `window`), optionally with this outcome. */
  template?: string | undefined;
  templateOutcome?: TemplateOutcome | undefined;
  /** Chats this AI agent handled (in `window`). */
  agent?: string | undefined;
  /** Chats where the AI called this tool (in `window`). */
  tool?: string | undefined;
  /** Narrows the three above; null = any time. */
  window?: InboxWindow | null | undefined;
}

export interface FilterOption {
  name: string;
  /** Chats it applies to in the window. */
  chats: number;
}

export interface FilterOptions {
  from: number;
  to: number;
  templates: FilterOption[];
  agents: FilterOption[];
  tools: FilterOption[];
}

export interface TemplateStatsRow {
  name: string;
  number: ThreadNumber;
  sent: number;
  /** Delivered or read. */
  delivered: number;
  read: number;
  failed: number;
  /** Answered in the same chat within 24 hours. */
  replied: number;
  /** Different chats it went to. */
  chats: number;
}

export interface TemplateStats {
  from: number;
  to: number;
  items: TemplateStatsRow[];
}

export type BlockHow = "undeliverable" | "one_tick";

export interface BlockNumberRow {
  number: ThreadNumber;
  /** Likely blocked, almost certain included. */
  blocked: number;
  certain: number;
  cameBack: number;
  heldBack: number;
  /** Days the number was failing for everyone — left out. */
  troubleDays: number;
}

export interface BlockedPerson {
  thread: ThreadSummary;
  since: number;
  how: BlockHow;
  certain: boolean;
  lastReachedAt: number | null;
  lastTemplate: string | null;
}

export interface BlockReport {
  from: number;
  to: number;
  /** The first IST day counted, "YYYY-MM-DD". */
  countedFrom: string;
  totals: {
    blocked: number;
    certain: number;
    cameBack: number;
    heldBack: number;
    skippedOnTroubleDays: number;
  };
  numbers: BlockNumberRow[];
  templates: { name: string; blockedAfter: number; heldBack: number }[];
  weeks: { weekStart: string; blocked: number }[];
  /** Newest first, at most 300. */
  people: BlockedPerson[];
}

export interface InsightsParams {
  numberId: number | null;
  window: InboxWindow;
}

export interface ThreadPage {
  items: ThreadSummary[];
  nextCursor: string | null;
}

export interface MessagePage {
  /** Oldest first. */
  items: WhatsAppMessage[];
  hasOlder: boolean;
  sessions: WhatsAppSession[];
}

export type PersonType = "customer" | "champ";

export interface PersonThreads {
  person: { type: PersonType; id: number; name: string; maskedPhone: string };
  threads: ThreadSummary[];
  lastSync: HistorySync | null;
}

export interface WhatsAppUnread {
  messages: number;
  threads: number;
}

export interface WhatsAppUpdates {
  /** The server's clock — the next call's `since`. */
  now: number;
  /** Threads changed since `since`. */
  threads: ThreadSummary[];
  /** The open thread's messages: new, or with a status changed since `since`. */
  messages: WhatsAppMessage[];
  sessions: WhatsAppSession[];
  unread: WhatsAppUnread;
}

/** A value the template needs, by its name; `prefill` is the person's name for name fields. */
export interface TemplateField {
  name: string;
  prefill: string;
}

/** A template that may be sent in a thread now. */
export interface WhatsAppTemplate {
  id: number;
  name: string;
  language: string;
  /** "UTILITY", "MARKETING", … ("" for Chatzy). */
  category: string;
  /** WATI: the text with `{{field}}` placeholders. Chatzy: "" — it has no template text. */
  body: string;
  /** "image", "video", "text", or "". */
  headerType: string;
  /** Every one is required. */
  fields: TemplateField[];
}

/** A send's answer. `created: false` — that clientMessageId was sent before; this is that message. */
export interface SendResult {
  message: WhatsAppMessage;
  created: boolean;
}

export interface CreatedTicket {
  ticketId: number;
  /** "HELP12345". */
  ticketNumber: string;
  /** false — the ticket already made from this message. */
  created: boolean;
}

/** WhatsApp's own limit on a message's text. */
export const TEXT_MAX_LENGTH = 4096;
/** The server's limit on a ticket title. */
export const TICKET_TITLE_MAX_LENGTH = 150;
/** The server's limit on one template value. */
export const TEMPLATE_VALUE_MAX_LENGTH = 1000;

export const whatsappKeys = {
  all: ["whatsapp"] as const,
  numbers: () => [...whatsappKeys.all, "numbers"] as const,
  threadLists: () => [...whatsappKeys.all, "threads"] as const,
  threadList: (params: ThreadListParams) =>
    [...whatsappKeys.threadLists(), params] as const,
  threads: () => [...whatsappKeys.all, "thread"] as const,
  thread: (id: number) => [...whatsappKeys.threads(), id] as const,
  messageLists: () => [...whatsappKeys.all, "messages"] as const,
  messages: (threadId: number) => [...whatsappKeys.messageLists(), threadId] as const,
  people: () => [...whatsappKeys.all, "person"] as const,
  person: (type: PersonType, id: number) => [...whatsappKeys.people(), type, id] as const,
  media: (messageId: number) => [...whatsappKeys.all, "media", messageId] as const,
  unread: () => [...whatsappKeys.all, "unread"] as const,
  sync: (id: number) => [...whatsappKeys.all, "sync", id] as const,
  templates: (threadId: number) => [...whatsappKeys.all, "templates", threadId] as const,
  filterOptions: (params: InsightsParams) =>
    [...whatsappKeys.all, "filter-options", params] as const,
  templateStats: (params: InsightsParams) =>
    [...whatsappKeys.all, "template-stats", params] as const,
  blockReport: (params: InsightsParams) =>
    [...whatsappKeys.all, "block-report", params] as const,
};

function setWindow(query: URLSearchParams, window: InboxWindow | null | undefined) {
  if (!window) return;
  query.set("from", window.from);
  query.set("to", window.to);
  if (window.fromTime) query.set("fromTime", window.fromTime);
  if (window.toTime) query.set("toTime", window.toTime);
}

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export function getWhatsAppNumbers(signal?: AbortSignal): Promise<WhatsAppNumber[]> {
  return api.get<WhatsAppNumber[]>("/whatsapp/numbers", withSignal(signal));
}

export function listThreads(
  params: ThreadListParams,
  cursor: string | null,
  signal?: AbortSignal,
): Promise<ThreadPage> {
  const query = new URLSearchParams({ filter: params.filter, limit: "30" });
  if (params.numberId !== null) query.set("numberId", String(params.numberId));
  if (params.search) query.set("search", params.search);
  if (params.template) {
    query.set("template", params.template);
    if (params.templateOutcome) query.set("templateOutcome", params.templateOutcome);
  }
  if (params.agent) query.set("agent", params.agent);
  if (params.tool) query.set("tool", params.tool);
  if (params.template || params.agent || params.tool) setWindow(query, params.window);
  if (cursor) query.set("cursor", cursor);
  return api.get<ThreadPage>(`/whatsapp/threads?${query}`, withSignal(signal));
}

function insightsQuery(params: InsightsParams): URLSearchParams {
  const query = new URLSearchParams();
  if (params.numberId !== null) query.set("numberId", String(params.numberId));
  setWindow(query, params.window);
  return query;
}

/** What the template, AI-agent and tool filters can be set to, with chat counts. */
export function getFilterOptions(
  params: InsightsParams,
  signal?: AbortSignal,
): Promise<FilterOptions> {
  return api.get<FilterOptions>(
    `/whatsapp/filter-options?${insightsQuery(params)}`,
    withSignal(signal),
  );
}

/** The Templates tab: per template and number, what became of the sends. */
export function getTemplateStats(
  params: InsightsParams,
  signal?: AbortSignal,
): Promise<TemplateStats> {
  return api.get<TemplateStats>(
    `/whatsapp/template-stats?${insightsQuery(params)}`,
    withSignal(signal),
  );
}

export function getThread(id: number, signal?: AbortSignal): Promise<ThreadDetail> {
  return api.get<ThreadDetail>(`/whatsapp/threads/${id}`, withSignal(signal));
}

export function getThreadMessages(
  threadId: number,
  before: number | null,
  signal?: AbortSignal,
): Promise<MessagePage> {
  const query = new URLSearchParams({ limit: "50" });
  if (before !== null) query.set("before", String(before));
  return api.get<MessagePage>(
    `/whatsapp/threads/${threadId}/messages?${query}`,
    withSignal(signal),
  );
}

export function markThreadRead(threadId: number): Promise<{ ok: true }> {
  return api.post<{ ok: true }>(`/whatsapp/threads/${threadId}/read`);
}

export function getPersonThreads(
  type: PersonType,
  id: number,
  signal?: AbortSignal,
): Promise<PersonThreads> {
  return api.get<PersonThreads>(`/whatsapp/people/${type}/${id}`, withSignal(signal));
}

export function getWhatsAppUpdates(
  params: {
    since: number | null;
    threadId: number | null;
    afterMessageId: number | null;
  },
  signal?: AbortSignal,
): Promise<WhatsAppUpdates> {
  const query = new URLSearchParams();
  if (params.since !== null) query.set("since", String(params.since));
  if (params.threadId !== null) query.set("threadId", String(params.threadId));
  if (params.afterMessageId !== null) {
    query.set("afterMessageId", String(params.afterMessageId));
  }
  const text = query.toString();
  const suffix = text ? `?${text}` : "";
  return api.get<WhatsAppUpdates>(`/whatsapp/updates${suffix}`, withSignal(signal));
}

/** A proxied attachment (WATI's need the provider token, which stays on bella). */
export function getMessageMedia(messageId: number, signal?: AbortSignal): Promise<Blob> {
  return requestBlob(`/whatsapp/messages/${messageId}/media`, withSignal(signal));
}

/**
 * Starts "Sync history" for a person: their chats from before the inbox went
 * live, from Chatzy and WATI. 202 with the sync. While one is queued or
 * running — or finished under 10 minutes ago — the same sync comes back
 * instead of a second run. 503: the background queue is unavailable.
 */
export function syncPersonHistory(type: PersonType, id: number): Promise<HistorySync> {
  return api.post<HistorySync>(`/whatsapp/people/${type}/${id}/sync`);
}

/** As `syncPersonHistory`, for a thread's person — or its number when nobody is linked. */
export function syncThreadHistory(threadId: number): Promise<HistorySync> {
  return api.post<HistorySync>(`/whatsapp/threads/${threadId}/sync`);
}

export function getHistorySync(id: number, signal?: AbortSignal): Promise<HistorySync> {
  return api.get<HistorySync>(`/whatsapp/syncs/${id}`, withSignal(signal));
}

/** The templates that may be sent in a thread now; empty when none may. `whatsapp.send`. */
export async function getThreadTemplates(
  threadId: number,
  signal?: AbortSignal,
): Promise<WhatsAppTemplate[]> {
  const page = await api.get<{ items: WhatsAppTemplate[] }>(
    `/whatsapp/threads/${threadId}/templates`,
    withSignal(signal),
  );
  return page.items;
}

/**
 * A reply. `clientMessageId` is a UUID made once per message and reused on a
 * retry: the server answers a repeat with the first send, so a retry never
 * sends twice. 409: not allowed now (the message says why); 400: empty, too
 * long; 429: too many sends this minute. A provider's refusal is a 201 with
 * the message failed.
 */
export function sendThreadText(
  threadId: number,
  input: { clientMessageId: string; text: string },
): Promise<SendResult> {
  return api.post<SendResult>(`/whatsapp/threads/${threadId}/messages`, input);
}

/** A template, with a value for every one of its fields. As `sendThreadText` otherwise. */
export function sendThreadTemplate(
  threadId: number,
  input: { clientMessageId: string; templateId: number; values: Record<string, string> },
): Promise<SendResult> {
  return api.post<SendResult>(`/whatsapp/threads/${threadId}/template-messages`, input);
}

/**
 * A HELP ticket from a customer's message: a support agent is assigned and
 * the message becomes its first note. Asking again for the same message
 * answers that ticket (`created: false`). 409: the chat has no customer.
 */
export function createTicketFromMessage(
  messageId: number,
  input: { title: string },
): Promise<CreatedTicket> {
  return api.post<CreatedTicket>(`/whatsapp/messages/${messageId}/ticket`, input);
}

/** The Blocks tab: who has probably blocked which of our numbers. */
export function getBlockReport(
  params: InsightsParams,
  signal?: AbortSignal,
): Promise<BlockReport> {
  return api.get<BlockReport>(
    `/whatsapp/block-report?${insightsQuery(params)}`,
    withSignal(signal),
  );
}
